import React from 'react';
import PropTypes from 'prop-types';
import * as Icons from './icons';
import Image from './image';
import Menu from './menu';
import {listenValue} from './value';
import {isMobile, hasPositionStickySupport} from '../environment';
import {intlShape} from 'react-intl';
import {reisy, use} from 'reisy';
import SearchButton from './searchbutton';
import Navigation from "../navigation/index";
import Link from "../components/link.js";
import Heading from './heading';

export const styles = reisy`
@namespace Header;

show: true;
showLogo: true;
showName: true;
showShadow: true;
showHR: true;
showScrollToContent: false;
showSearch: false;
Branding: $Settings.Branding;
JackInTheBox: $Settings.JackInTheBox;
Height: 80;

navbarCollapsed: none;
navbarBehavior: static transparent;

@keyframes Floating {
	0% {
		transform: translate(0, 0em);
	}
	60% {
		transform: translate(0, .5em);
	}
	100% {
		transform: translate(0, 0em);
	}
}

Shadow {
	@extends Helpers.AbsFull;
	background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.05) 84px, rgba(0, 0, 0, 0.05) 25%, rgba(0, 0, 0, 0.55));
}

defaultBackground1 { background-color: #FFBC67; }
defaultBackground2 { background-color: #233656; }
defaultBackground3 { background-color: #88BF63; }
defaultBackground4 { background-color: #685C79; }
defaultBackground5 { background-color: #455C7B; }
defaultImageBackground { background-color: #585D67; }

headerUser {
	@extends AlignLeft;
}

headerPublisher {
	@extends AlignCenter;	
}

header {
	@extends Helpers.FontSize, Typography.Header, userBackgroundColor;
	width: 100%;
	max-height: 60em;
	position: relative;

	$Media.short {
		height: 300px !important;
	}

	hr {
		width: 100%;
		border: 0;
		height: 1px;
		margin: .8em auto 0;
		background-color: #fff;
	}

	a {
		text-decoration: none;
	}

	&, & *, & *::after, & *::before {
		box-sizing: border-box;
	}
}

offsetHeader {
	margin-top: $Navigation.DefaultHeight;
}

AlignLeft {
	text-align: left;
}

AlignCenter {
	text-align: center;

	& $headerWrapUser {
		left: 0;
		right: 0;
	}

	& $headerContentUser $MenuLinks.list,
	& $headerWrap,
	& $userBar {
		justify-content: center;
	}

	& $headerContent {
		margin-right: auto;
		margin-left: auto;
	}
}

AlignRight {
	text-align: right;

	& $headerWrapUser {
		right: 0;
	}

	& $headerContentUser $MenuLinks.list,
	& $headerWrap,
	& $userBar {
		justify-content: flex-end;
	}
}

Clipper {
	@extends Helpers.Clip;
	position: absolute;
	width: 100%;
	height: 100%;
}

heading {
	@extends Typography.HeaderHeading;
	margin: 0;
}
slogan {
	@extends Typography.HeaderSlogan;
	margin: 0;
	$Media.short {
		margin-top: 0;
	}
}

background  {
	@extends Helpers.FontSize, userBackgroundColor, defaultImageBackground;
	position: fixed;
	z-index: -1;
	top: 0;
	left: 50%;
	width: 100%;
	height: 100%;
	max-height: 60em;
	background-repeat: no-repeat;
	background-position: center center;
	background-size: cover;
	transform: translate(-50%, 0);
	will-change: transform;
	max-width: $Settings.KoskMaxWidth;

	$Media.short {
		height: 300px !important;
		margin-bottom: -300px !important;
	}

	& ~ $Header.header {
		background-color: transparent;
		max-width: $Settings.KoskMaxWidth;
		margin-left: auto;
		margin-right: auto;
	}
}

offsetBackground {
	top: $Navigation.DefaultHeight;
}

userBackgroundColor {}

Sticky {
	position: sticky;
	transform: none;
	margin: 0 auto;
	left: 0;
}

Static {
	position: relative;
}

headerLogo {
	margin-bottom: 1em;
	img {
		margin: 0 auto;
		max-height: 100px;
	}
}
headerWrap {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	position: absolute;
	bottom: 0;
	width: 100%;
	max-width: $Settings.KoskMaxWidth;
	margin: 0 auto;
	z-index: 1;
}
headerWrapUser {
	max-width: 50em;
}

headerContent {
	box-sizing: border-box;
	max-width: 51em;
	padding: 0 .5em;
	margin: 0 0 2em;

	$Media.small {
		padding: 0 2em;
	}
	$Media.short {
		margin-bottom: 1em;
	}
}

headerContentUser {
	/* In case you are wondering:
	 * this is here because setting this in the MenuLink messes up
	 * the css order of the headerContentUser class
	 */
	$MenuLinks.list {
		justify-content: flex-start;
	}
}

desc {
	font-family: $Fonts.text;
	margin-top: 1em;
}

anchor {
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
}

userBar {
	display: flex;
	align-items: center;
	margin-top: 2em;
}
userBarAvatar {
	display: inline-block;
	width: 2em;
	height: 2em;
	margin-right: .25em;
	background-size: cover;
	background-position: center center;
	border-radius: 100%;
	vertical-align: middle;
	overflow: hidden;
}
userBarText {
	font: .85em $Fonts.aside;
	color: $Colors.TextLight;
	text-align: right;
}
userBarCounts {
	flex-grow: 1;
}
scrollDown {
	text-align: center;
	cursor: pointer;
}
scrollDownIcon {
	animation: $Floating 3.1s infinite cubic-bezier(0.41, 0.01, 0.57, 0.96);
	display: block;
	margin: 0 auto;
	width: 2.5em;
	height: 2.5em;
	padding-Bottom: 1em;
	fill: #fff;
}
search {
	position: absolute;
	bottom: 0;
	right: 0;
	padding: 1em;
	max-width: 12em;
}
`;

const navigationNeedsOffset = () =>
	reisy`@namespace Kosk;`.navbarBehavior.indexOf("transparent") === -1 &&
	reisy`@namespace Kosk;`.navbarBehavior.indexOf("static") === -1 &&
	reisy`@namespace Kosk;`.navbarBehavior.indexOf("absolute") === -1
;

const headerHeight = (windowHeight, percent) => (isMobile
	? `${windowHeight * percent / 100}px`
	: `${percent}vh`);

const _Header = ({publisher, type, windowHeight, onScrollToContent}, {intl: {formatMessage}}) => {
	const backgroundColor = styles[`defaultBackground${Math.floor(parseInt(publisher.id.slice(-1), 10) / 2)}`];

	return (
		styles.show
		? <div
			{...use(
				styles.header,
				type === 'user' ? styles.headerUser : styles.headerPublisher,
				!publisher.background && backgroundColor,
				styles.showShadow && styles.Shadow,
				navigationNeedsOffset() && styles.offsetHeader,
				{height: headerHeight(windowHeight, styles.Height)}
			)}>
			<div {...use(styles.headerWrap, type === 'user' ? styles.headerWrapUser : styles.headerWrapPublisher)}>
				<div {...use(styles.headerContent, type === 'user' ? styles.headerContentUser : styles.headerContentPublisher)}>
					{(styles.showLogo && publisher.image && type === 'publisher') && <Image style={styles.headerLogo} name="default" image={publisher.image} lazy />}
					{styles.showName && <Heading lvl={1} {...use(styles.heading)}>{publisher.title || publisher.name}</Heading>}
					{publisher.slogan ? <p {...use(styles.slogan)}>{publisher.slogan}</p> : null}
					{styles.showHR &&
						(styles.showName && (publisher.title || publisher.name) || publisher.slogan) &&
						(publisher.description || publisher.menues && publisher.menues.links)
						? <hr />
						: null}
					{publisher.description ? <div {...use(styles.desc)}>{publisher.description}</div> : null}
					{publisher.menues ? <Menu position="links" menues={publisher.menues} /> : null}
					{type === 'user'
						? <div {...use(styles.userBar)}>
							{publisher.image && styles.showLogo
								? <Image style={styles.userBarAvatar} name="default" image={publisher.image} lazy />
								: null}

							{styles.showName ? <span {...use(styles.userBarText)}>{publisher.name}</span> : null}
						</div>
						: null}
				</div>
				{styles.showScrollToContent
					? <div {...use(styles.scrollDown)} onClick={onScrollToContent}>
						<Icons.ChevronDown {...use(styles.scrollDownIcon)} />
					</div>
					: null}

				{styles.showSearch && <div {...use(styles.search)}><SearchButton /></div>}
				<Navigation styles={styles} />
			</div>
		</div>
		: null
	);
};

_Header.propTypes = {
	publisher: PropTypes.object.isRequired,
	type: PropTypes.string.isRequired,
	windowHeight: PropTypes.number,
	onScrollToContent: PropTypes.func,
};

_Header.contextTypes = {
	intl: intlShape,
};

export const Header = listenValue(_Header, 'windowHeight');

const _Background = ({windowHeight, background}) => (
	!background || !background.image ? null :
	<Image style={[
		styles.background,
		{
			height: headerHeight(windowHeight, styles.Height),
			marginBottom: `-${headerHeight(windowHeight, styles.Height)}`,
		},
		navigationNeedsOffset() && styles.offsetBackground,
		styles.JackInTheBox && (hasPositionStickySupport ? styles.Sticky : styles.Static),
	]} image={background.image} cover />
);

export const Background = listenValue(_Background, "windowHeight");



// WEBPACK FOOTER //
// ./lib/components/header.js